/**
 * ActionSuggestions — Full-width "option card" list for agent-suggested
 * next steps.
 *
 * Rendered inside agent message bubbles via the MessageWidgets registry.
 * Each card stacks a short heading (the `label` shown in the user's bubble
 * after click) above a muted preview of the verbose `prompt` that will be
 * sent to the agent — so the user can read what they'll actually be asking
 * for without needing to hover. Cards stack vertically and fill the bubble
 * width so multi-line prompts wrap naturally and the choices read like a
 * checklist of next steps rather than a row of cramped pills.
 *
 * Supports keyboard navigation (ArrowUp / ArrowDown / Enter).
 */
import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import {
  PlayArrowRounded as PlayIcon,
  FilterList as FilterIcon,
  AccountTree as MapIcon,
  AutoFixHigh as CleanIcon,
  TravelExplore as ExploreIcon,
  Transform as TransformIcon,
  Schema as SchemaIcon,
  ArrowForwardRounded as ArrowIcon,
} from '@mui/icons-material';

// ── Types ────────────────────────────────────────────────────────────

export interface SuggestedAction {
  id: string;
  label: string;
  prompt: string;
  type: 'action' | 'choice' | 'parameterized';
  icon?: string;
}

export interface ActionSuggestionsProps {
  actions: SuggestedAction[];
  /**
   * Invoked when a card is clicked. `prompt` is the verbose instruction
   * sent to the agent; `label` is the short heading the UI uses for the
   * user's bubble (via `display_text` in message metadata) so the chat
   * history reads as the user's *choice*, not the rephrased instruction.
   */
  onActionClick: (prompt: string, label?: string) => void;
  disabled?: boolean;
}

// ── Icon mapping ─────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactElement> = {
  play: <PlayIcon sx={{ fontSize: 16 }} />,
  filter: <FilterIcon sx={{ fontSize: 16 }} />,
  map: <MapIcon sx={{ fontSize: 16 }} />,
  clean: <CleanIcon sx={{ fontSize: 16 }} />,
  explore: <ExploreIcon sx={{ fontSize: 16 }} />,
  transform: <TransformIcon sx={{ fontSize: 16 }} />,
  schema: <SchemaIcon sx={{ fontSize: 16 }} />,
};

// ── Component ────────────────────────────────────────────────────────

const ActionSuggestions: React.FC<ActionSuggestionsProps> = ({
  actions,
  onActionClick,
  disabled = false,
}) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, actions.length - 1));
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && activeIndex >= 0 && activeIndex < actions.length) {
        e.preventDefault();
        const a = actions[activeIndex];
        onActionClick(a.prompt, a.label);
      }
    },
    [actions, activeIndex, disabled, onActionClick],
  );

  if (!actions?.length) return null;

  const accentColor = theme.palette.primary.main;

  return (
    <Box
      ref={containerRef}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      onBlur={() => setActiveIndex(-1)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        mt: 1.5,
        outline: 'none',
        ...(disabled && { opacity: 0.5, pointerEvents: 'none' }),
      }}
    >
      {actions.map((action, i) => {
        const icon = action.icon ? ICON_MAP[action.icon] : null;
        const isActive = activeIndex === i;
        // Hide the description when it's just a re-statement of the label
        // (avoids noisy "Map subjects to ODM / Map subjects to ODM" cards).
        const showDescription =
          !!action.prompt && action.prompt.trim() !== action.label.trim();

        return (
          <Box
            key={action.id || i}
            onClick={() => !disabled && onActionClick(action.prompt, action.label)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              width: '100%',
              px: 1.25,
              py: 1,
              border: 1,
              borderColor: isActive ? accentColor : 'divider',
              borderRadius: 0.75,
              cursor: 'pointer',
              transition: 'all 0.15s',
              bgcolor: isActive ? alpha(accentColor, 0.06) : 'background.paper',
              '&:hover': {
                borderColor: accentColor,
                bgcolor: alpha(accentColor, 0.04),
                boxShadow: `0 0 0 1px ${alpha(accentColor, 0.15)}`,
                '& .action-arrow': { opacity: 1, transform: 'translateX(2px)' },
              },
            }}
          >
            {icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: accentColor,
                  mt: 0.125,
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: 'text.primary',
                }}
              >
                {action.label}
              </Typography>
              {showDescription && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.25,
                    fontSize: 11.5,
                    lineHeight: 1.45,
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {action.prompt}
                </Typography>
              )}
            </Box>
            <ArrowIcon
              className="action-arrow"
              sx={{
                fontSize: 16,
                color: accentColor,
                opacity: isActive ? 1 : 0.35,
                transition: 'opacity 0.15s, transform 0.15s',
                mt: 0.25,
                flexShrink: 0,
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default ActionSuggestions;
